import type { PropsWithChildren, ReactElement } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type {
    GetAllStandardTextsQueryVariables,
    StandardText,
} from '@buying/library';

import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { GetAllStandardTextsDocument, GraphClientNames } from '@buying/library';

interface StandardTextsContextType {
    comments: Array<StandardText>;
    packingTexts: Array<StandardText>;
    isLoadingStandardTexts: boolean;
}

interface StandardTextsProviderProps {
    brandNumber: GetAllStandardTextsQueryVariables['brandNumber'];
    commentsTypeName?: GetAllStandardTextsQueryVariables['typeName'];
    packingTypeName?: GetAllStandardTextsQueryVariables['typeName'];
}

const StandardTextsContext = createContext<StandardTextsContextType | null>(
    null
);

const useStandardTexts = (): StandardTextsContextType => {
    const context = useContext(StandardTextsContext);
    if (!context)
        throw new Error(
            'useStandardTexts must be used within StandardTextsProvider'
        );
    return context;
};

const normalizeStandardText = (
    standardTexts: Array<StandardText>
): Array<StandardText> => {
    if (standardTexts.length === 0) return [];

    return standardTexts.map((text) => ({
        ...text,
        standardText: text.standardText.replaceAll('\r\n', '\n').trim(),
    }));
};

const StandardTextsProvider = ({
    brandNumber,
    commentsTypeName = 'PO_COMMENTS',
    packingTypeName = 'PO_PACKING',
    children,
}: PropsWithChildren<StandardTextsProviderProps>): ReactElement => {
    const { data: packingData, networkStatus: packingNetworkStatus } = useQuery(
        GetAllStandardTextsDocument,
        {
            context: {
                clientName: GraphClientNames['bestone-bi4-buying-order-bff'],
            },
            variables: {
                brandNumber: brandNumber,
                typeName: packingTypeName,
                isDeleted: false,
            },
        }
    );

    const { data: commentsData, networkStatus: commentsNetworkStatus } =
        useQuery(GetAllStandardTextsDocument, {
            context: {
                clientName: GraphClientNames['bestone-bi4-buying-order-bff'],
            },
            variables: {
                brandNumber: brandNumber,
                typeName: commentsTypeName,
                isDeleted: false,
            },
        });

    const isLoadingStandardTexts =
        packingNetworkStatus === NetworkStatus.loading ||
        packingNetworkStatus === NetworkStatus.setVariables ||
        commentsNetworkStatus === NetworkStatus.loading ||
        commentsNetworkStatus === NetworkStatus.setVariables;

    const contextValue: StandardTextsContextType = useMemo(
        () => ({
            comments: normalizeStandardText(commentsData?.standardTexts ?? []),
            packingTexts: normalizeStandardText(
                packingData?.standardTexts ?? []
            ),
            isLoadingStandardTexts,
        }),
        [
            commentsData?.standardTexts,
            isLoadingStandardTexts,
            packingData?.standardTexts,
        ]
    );

    return (
        <StandardTextsContext.Provider value={contextValue}>
            {children}
        </StandardTextsContext.Provider>
    );
};

export { StandardTextsProvider, useStandardTexts };
